import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { DEV_AUTH_ENABLED } from "../lib/devAuth";
import { useGuestIdentity } from "../lib/guestIdentity";
import { ReadableCard, ReadableNavButton } from "../components/Sticker";

const PAGE_TITLE = "Page not found — rsvp.dance";
const SHELL_PADDING_BOTTOM = DEV_AUTH_ENABLED
  ? "pb-[calc(9.75rem+env(safe-area-inset-bottom,0px))]"
  : "pb-[calc(5rem+env(safe-area-inset-bottom,0px))]";

export default function NotFoundPage() {
  const { account, signOut } = useAuth();
  const { clearGuest } = useGuestIdentity();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  function clearSavedLogin() {
    signOut();
    clearGuest();
    toast("Cleared saved login on this device.");
    nav("/login?next=/events", { replace: true, state: { from: location.pathname } });
  }

  function signOutOnly() {
    signOut();
    clearGuest();
    toast("Signed out.");
    nav("/events", { replace: true });
  }

  return (
    <div className={`min-h-[100dvh] bg-canvas text-ink font-body flex flex-col ${SHELL_PADDING_BOTTOM}`}>
      <header className="max-w-page mx-auto w-full px-4 pt-4 pb-2 shrink-0">
        <ReadableNavButton as={Link} to="/events" data-testid="not-found-back-events">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          Events
        </ReadableNavButton>
      </header>

      <main
        className="flex-1 flex flex-col justify-center w-full max-w-page mx-auto px-4 py-6 sm:py-10 sm:pb-12"
        aria-labelledby="not-found-heading"
      >
        <ReadableCard className="w-full max-w-md mx-auto text-center" data-testid="not-found-card">
          <div className="px-5 pt-6 sm:px-6 sm:pt-6 space-y-3">
            <h1
              id="not-found-heading"
              className="font-ui font-semibold text-[22px] sm:text-[26px] leading-tight text-balance"
            >
              This page isn&apos;t here
            </h1>
            <p
              id="not-found-status"
              className="font-ui font-medium text-[14px] sm:text-[15px] text-ink/80 leading-relaxed max-w-[65ch] mx-auto text-pretty"
              role="status"
              aria-live="polite"
            >
              The link may be old, or the event may have ended. Browse what&apos;s on tonight — no account
              needed.
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-black/[0.06] px-5 pb-6 sm:px-6 sm:pb-6 flex flex-col gap-3">
            <div className="flex flex-col items-stretch gap-3">
              <ReadableNavButton
                as={Link}
                to="/events"
                variant="primary"
                className="w-full"
                data-testid="not-found-browse-btn"
              >
                <CalendarDays className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                Browse events
              </ReadableNavButton>

              {account ? (
                <ReadableNavButton
                  as={Link}
                  to="/my-events"
                  className="w-full"
                  data-testid="not-found-my-events-btn"
                >
                  My events
                </ReadableNavButton>
              ) : null}
            </div>

            <div className="pt-3 border-t border-black/[0.06] space-y-3" aria-labelledby="not-found-recovery-label">
              <p id="not-found-recovery-label" className="font-ui font-medium text-[13px] text-ink/70 leading-snug">
                Still stuck? Saved login on this device may be pointing at an old page.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                <ReadableNavButton
                  type="button"
                  variant="dangerOutline"
                  onClick={clearSavedLogin}
                  data-testid="not-found-reset-btn"
                  className="w-full sm:w-auto"
                >
                  <RefreshCw className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                  Clear saved login
                </ReadableNavButton>
                {account ? (
                  <ReadableNavButton
                    type="button"
                    onClick={signOutOnly}
                    data-testid="not-found-signout-btn"
                    className="w-full sm:w-auto"
                  >
                    <LogOut className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                    Sign out only
                  </ReadableNavButton>
                ) : null}
              </div>
            </div>

            <p className="font-ui font-medium text-[13px] text-ink/75 leading-snug">
              Need help?{" "}
              <Link
                to="/faq"
                className="text-plum font-semibold underline underline-offset-2 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
              >
                FAQ
              </Link>
              {" · "}
              <Link
                to="/support"
                className="text-plum font-semibold underline underline-offset-2 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
              >
                Support
              </Link>
            </p>
          </div>
        </ReadableCard>
      </main>
    </div>
  );
}
